import React from 'react';
import {
  COMMA_LIST_SEPARATOR,
  COMPLAINTS_STATUS,
  DATA_STATUS,
  LOADING_PROPERTY_COMPLAINTS,
  complaintTypeLabelFactory,
} from '@/constants';
import { AnimatedText } from '@/widgets/bouncing-box-loader/animated-text';
import styled from 'styled-components';
import {
  IssueFreeMessage,
  IssueSummaryReportContainer,
  IssueSummaryReportContent,
  IssueSummaryReportRow,
  IssueSummaryTitle,
} from './common-report-components';

const ComplaintsLoadingContainer = styled(IssueSummaryReportContainer)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ComplaintsLoadingText = styled(AnimatedText)`
  margin-top: unset;
`;

function ComplaintsNotLoadedMessage({ isError, className, css }) {
  return (
    <ComplaintsLoadingContainer className={className} css={css}>
      {!isError ? (
        <ComplaintsLoadingText loadingText={LOADING_PROPERTY_COMPLAINTS} />
      ) : (
        <>
          Something went wrong while loading complaints data. Refresh page to
          try again.
        </>
      )}
    </ComplaintsLoadingContainer>
  );
}

/**
 * Renders a box with a summary report of open complaint counts for a property on the
 * property details screen. Displays the types of the two most recent complaints.
 * @param {string} props.className CSS classes to apply to the container element of the report.
 * Used to allow styling via `styled-components`
 * @param {string} props.css CSS styles to apply to the container element of the report.
 * Used to allow styling via `styled-components`.
 * @param {object[]} props.complaints Complaints to summarize, or a DATA_STATUS value
 * if complaints are not loaded.
 * @param {function} props.onClick Handler for clicks on the report
 * @param {number} props.numRecentComplaintsDisplayed The number of recent complaints
 * to display in the box.
 * @returns {JSX.Element}
 * @constructor
 */
export function ComplaintSummaryReport(props) {
  const {
    className,
    css,
    complaints,
    onClick,
    numRecentComplaintsDisplayed = 2,
  } = props;
  if (
    !complaints ||
    [DATA_STATUS.inProgress, DATA_STATUS.dataError].includes(complaints)
  ) {
    return (
      <ComplaintsNotLoadedMessage
        className={className}
        css={css}
        isError={complaints === DATA_STATUS.dataError}
      />
    );
  }

  const noComplaintsMessage = (
    <IssueFreeMessage>
      We found no open complaints for this property
    </IssueFreeMessage>
  );

  const recentComplaints = complaints
    .filter((complaint) => complaint.status === COMPLAINTS_STATUS.OPEN)
    .sort(
      (c1, c2) =>
        new Date(c2.submissionDate).getTime() -
        new Date(c1.submissionDate).getTime()
    );

  const numHiddenComplaints = Math.max(
    0,
    recentComplaints.length - numRecentComplaintsDisplayed
  );

  return (
    <IssueSummaryReportContainer
      as={onClick && 'button'}
      className={className}
      css={css}
      free={!recentComplaints.length}
      onClick={onClick}
    >
      <IssueSummaryTitle>
        Open Complaints ({recentComplaints.length})
      </IssueSummaryTitle>
      {!recentComplaints.length ? (
        noComplaintsMessage
      ) : (
        <>
          <IssueSummaryReportContent>
            {recentComplaints
              .slice(0, numRecentComplaintsDisplayed)
              .map(({ complaintTypes, complaintId }) => (
                <IssueSummaryReportRow key={complaintId}>
                  {complaintTypes
                    .map(complaintTypeLabelFactory)
                    .join(COMMA_LIST_SEPARATOR)}
                </IssueSummaryReportRow>
              ))}
          </IssueSummaryReportContent>
          {numHiddenComplaints > 0 && `+ ${numHiddenComplaints} more`}
        </>
      )}
    </IssueSummaryReportContainer>
  );
}
